import { NgbAccordionModule, NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { CoreModule, LocalizationService } from '@abp/ng.core';
import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  HostListener,
  inject,
  Injector,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { DialogBoxComponent, FilterByPipe } from '@digiwall/ui-kit';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  DayBookDto,
  SupplierOrderDraftDto,
  SupplierOrderPreparationResultDto,
  SupplierPurchaseOrderService,
} from '@digiwall/goliath-documents/proxy';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DwEnumSelectDirective,
  DwInputRequiredDirective,
  ErrorFormControlDirective,
  LoadingService,
  OdysseyOffcanvas,
} from '@digiwall/core';
import {
  GoliathDocumentsEnumerationTypeStore,
  SupplierOrderGrouping,
  SupplierOrderStatus,
} from '../../../enumeration-types';
import { SupplierOrderDraftGridComponent } from './supplier-order-draft-grid/supplier-order-draft-grid.component';
import { ToasterService } from '@abp/ng.theme.shared';
import { WindowUnloadService } from '../../../services';
import { EnumerationTypeDto } from '@digiwall/core/proxy';
import { CompatibleSupplierOrderSelectionComponent } from './compatible-supplier-order-selection/compatible-supplier-order-selection.component';

const LOADING_KEY = 'SUPPLIER_ORDERS_PREPARATION';

enum PreparationStep {
  Selection,
  Generation,
}

@Component({
  selector: 'document-supplier-order-creation-dialog',
  templateUrl: './supplier-order-creation-dialog.component.html',
  imports: [
    CommonModule,
    CoreModule,
    DialogBoxComponent,
    NgbAccordionModule,
    NgSelectModule,
    DwInputRequiredDirective,
    ErrorFormControlDirective,
    SupplierOrderDraftGridComponent,
    DwEnumSelectDirective,
    FilterByPipe,
  ],
})
export class SupplierOrderCreationDialogComponent implements OnInit {
  protected docEnumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  private formBuilder = inject(FormBuilder);
  private loadingService = inject(LoadingService);
  private activeCanvas = inject(NgbActiveOffcanvas);
  private offCanvasService = inject(OdysseyOffcanvas);
  private supplierPurchaseOrderService = inject(SupplierPurchaseOrderService);
  private toasterService = inject(ToasterService);
  private localizationService = inject(LocalizationService);
  private windowUnloadService = inject(WindowUnloadService);
  private injector = inject(Injector);

  public daybooks = model<DayBookDto[]>();
  public selectedLineIds = model<string[]>([]);

  protected form: FormGroup;
  protected currentStep: PreparationStep = PreparationStep.Selection;
  protected PreparationStep = PreparationStep;
  protected supplierOrderDrafts = signal<SupplierOrderDraftDto[]>([]);
  protected correlationId: string | null = null;

  constructor() {
    effect(() => {
      const daybooks = this.daybooks();
      if (daybooks.length == 0) return;

      const currentDayBookId = this.form.get('daybookId')?.value;
      if (currentDayBookId == null || !daybooks.some(x => x.id == currentDayBookId)) {
        this.form.get('daybookId')?.setValue(daybooks[0].id);
      }
    });
  }

  ngOnInit(): void {
    this.initForm();
  }

  protected prepareSupplierOrders() {
    if (!this.form.valid) return;

    this.currentStep = PreparationStep.Generation;
    this.loadingService.setLoading(LOADING_KEY, true);

    let requestBody = Object.assign(this.form.getRawValue(), {
      lineIds: this.selectedLineIds(),
    });

    this.supplierPurchaseOrderService.prepareFromCustomerOpenOrderLines(requestBody).subscribeOnce(
      preparationResult => {
        this.loadingService.setLoading(LOADING_KEY, false);
        this.correlationId = preparationResult.correlationId;

        if (preparationResult.succeeded) {
          this.supplierOrderDrafts.set(preparationResult.successResult);
        } else {
          this.handleMultipleCompatibleSupplierOrders(preparationResult, requestBody);
        }
      },
      () => {
        this.currentStep = PreparationStep.Selection;
        this.loadingService.setLoading(LOADING_KEY, false);
      },
    );
  }

  private handleMultipleCompatibleSupplierOrders(
    preparationResult: SupplierOrderPreparationResultDto,
    requestBody: any,
  ) {
    var canvaRef = this.offCanvasService.open(
      CompatibleSupplierOrderSelectionComponent,
      this.injector,
      {
        position: 'end',
        panelClass: 'w-100 w-sm-75 w-3xl-50 ',
        backdrop: 'static',
      },
    );
    (
      canvaRef.componentInstance as CompatibleSupplierOrderSelectionComponent
    ).selectedSupplierOrderByGroupKey.set(preparationResult.failedResult.value);

    canvaRef.dismissed.subscribe(() => {
      this.currentStep = PreparationStep.Selection;
      this.supplierPurchaseOrderService
        .cancelPreparationFromCustomerOpenOrderLinesByRequest({
          correlationId: this.correlationId,
        })
        .subscribeOnce();
    });

    canvaRef.closed.subscribe(selectedSupplierOrders => {
      requestBody = Object.assign(requestBody, {
        selectedSupplierOrderByGroupKey: selectedSupplierOrders,
        correlationId: this.correlationId,
      });

      this.loadingService.setLoading(LOADING_KEY, true);

      this.supplierPurchaseOrderService
        .prepareFromCustomerOpenOrderLines(requestBody)
        .subscribeOnce(
          preparationResult => {
            this.loadingService.setLoading(LOADING_KEY, false);

            if (preparationResult.succeeded) {
              this.supplierOrderDrafts.set(preparationResult.successResult);
            }
          },
          () => {
            this.currentStep = PreparationStep.Selection;
            this.loadingService.setLoading(LOADING_KEY, false);
          },
        );
    });
  }

  protected onCancel(): void {
    this.supplierPurchaseOrderService
      .cancelPreparationFromCustomerOpenOrderLinesByRequest({
        correlationId: this.correlationId,
      })
      .subscribeOnce(() => {
        this.activeCanvas.dismiss();
      });
  }

  protected generateSupplierOrders(): void {
    this.loadingService.setLoading(LOADING_KEY, true);

    const requestBody = Object.assign(this.form.getRawValue(), {
      supplierOrderDrafts: this.supplierOrderDrafts(),
      correlationId: this.correlationId,
    });

    this.supplierPurchaseOrderService
      .generationFromCustomerOpenOrderLines(requestBody)
      .subscribeOnce(
        () => {
          this.loadingService.setLoading(LOADING_KEY, false);
          this.toasterService.success(
            this.localizationService.instant(
              'Doc::SupplierOrdersGenerated',
              this.supplierOrderDrafts().length.toString(),
            ),
          );
          this.activeCanvas.close(true);
        },
        () => {
          this.loadingService.setLoading(LOADING_KEY, false);
        },
      );
  }

  protected filterSupplierOrderStatus = (status: EnumerationTypeDto) => {
    return status.id != SupplierOrderStatus.Closed;
  };

  private initForm() {
    this.form = this.formBuilder.group({
      daybookId: [null, Validators.required],
      grouping: [SupplierOrderGrouping.GroupByOrder, Validators.required],
      statusId: [SupplierOrderStatus.Draft, Validators.required],
    });
  }

  @HostListener('window:beforeunload')
  onWindowUnload() {
    this.windowUnloadService.makeUnloadRequest(
      'POST',
      `/api/goliath-documents/supplier-purchase-order/cancel-preparation-from-customer-open-order-lines`,
      { result: this.correlationId },
    );
  }
}
